(ns manage.slate-editor.core
  (:require ["@mui/system" :refer [styled]]
            ["react" :as react]
            ["slate" :as slate]
            ["slate-history" :as slate-history]
            ["slate-react" :as slate-react]
            [cljs-bean.core :refer [->clj ->js]]
            [manage.slate-editor.codec :as codec]
            [manage.slate-editor.common :as common]
            [manage.slate-editor.leaf :as leaf]
            [manage.slate-editor.macro-node :as macro]
            [manage.slate-editor.mentions :as mentions]
            [manage.slate-editor.mentions-node :as mentions-node]
            [manage.slate-editor.query-param-node :as query-param]
            [manage.slate-editor.util :as util]
            [uix.core :refer [$ defui]]))

(def ^:private mui-internal-prop-names
  #{"ownerState" "theme" "sx" "as"})

(def styled-slate-readonly
  ;; Documentation at https://mui.com/system/styled/
  ((styled slate-react/Editable
           #js {:name "SlateEditableReadOnly"
                :shouldForwardProp (fn [prop]
                                     (and (not (contains? mui-internal-prop-names prop))
                                          (not (contains? #{"error"} prop))))
                :slot "Root"
                :overridesResolver (fn [props styles]
                                     #js [(-> styles .-root)
                                          (when (-> props .-error) (-> styles .-error))])})
   (fn [^js _props]
     #js {})))

(def styled-slate-editable
  ;; Documentation at https://mui.com/system/styled/
  ((styled slate-react/Editable
           #js {:name "SlateEditable"
                :shouldForwardProp (fn [prop]
                                     (and (not (contains? mui-internal-prop-names prop))
                                          (not (contains? #{"error"} prop))))
                :slot "Root"
                :overridesResolver (fn [props styles]
                                     #js [(-> styles .-root)
                                          (when (-> props .-error) (-> styles .-error))])})
   (fn [^js _props]
     #js {})))

(defui slate-editor [{:keys [editor-ref class data-testid error default-value value multiline allow-query-params allow-macros allow-mentions allowed-mentions-ids
                             placeholder on-change on-focus on-blur on-key-down on-key-press is-macro-valid? auto-focus? sx]}]
  (let [[^js editor _] (react/useState (fn []
                                         (-> (slate/createEditor)
                                             slate-history/withHistory
                                             slate-react/withReact
                                             (cond-> allow-macros (macro/with-macro-node-support is-macro-valid?))
                                             (cond-> allow-query-params query-param/with-query-param-node-support)
                                             (cond-> allow-mentions mentions/withMentions)
                                             (cond-> (not multiline) util/with-single-line))))

        ;; Provides the editor's ref to the parent component.
        ;; It may be improved by moving the parts which use editor-ref as children.
        _ (react/useEffect (fn []
                             (set! (.-current editor-ref) editor)
                             (fn []
                               (set! (.-current editor-ref) nil)))
                           #js [editor])

        [initial-value _] (react/useState (fn []
                                            (let [v (or default-value value "")]
                                              (cond-> v
                                                (string? v)
                                                (codec/text->slate-value {:allow-query-params allow-query-params
                                                                          :allow-macros allow-macros
                                                                          :allow-mentions allow-mentions
                                                                          :allowed-mentions-ids allowed-mentions-ids
                                                                          :is-macro-valid? is-macro-valid?})

                                                true
                                                clj->js))))

        ;; We use useRef instead of useState to avoid re-rendering on focus or on blur.
        focused-ref (react/useRef false)
        on-focus (react/useCallback (fn [^js event]
                                      (set! (.-current focused-ref) true)
                                      (when (some? on-focus)
                                        (on-focus editor event)))
                                    #js [editor on-focus])
        on-blur (react/useCallback (fn [^js event]
                                     (set! (.-current focused-ref) false)
                                     (when (some? on-blur)
                                       (on-blur editor event)))
                                   #js [editor on-blur])

        ;; Changes Slate's value when the value prop is changing while not having the focus
        ;; (which means an external source of change).
        _ (react/useEffect (fn []
                             (let [focused (.-current focused-ref)]
                               (when-not focused
                                 (let [v (or value "")
                                       new-children (cond-> v
                                                      (string? v)
                                                      (codec/text->slate-value {:allow-query-params allow-query-params
                                                                                :allow-macros allow-macros
                                                                                :allow-mentions allow-mentions
                                                                                :allowed-mentions-ids allowed-mentions-ids
                                                                                :is-macro-valid? is-macro-valid?}))]
                                   ;; This is Slate's recommended way of updating its value from an external source.
                                   (set! (.-children editor) (clj->js new-children))
                                   ;; Ensures the cursor is pointing at an existing place.
                                   (slate/Transforms.select editor (slate/Editor.end editor #js []))
                                   ;; Reset the undo stack (undocumented but necessary hack)
                                   ;; More information at https://github.com/ianstormtaylor/slate/blob/main/packages/slate-history/src/with-history.ts
                                   (set! (-> editor .-history .-undos .-length) 0)
                                   ;; Trigger a re-render.
                                   (.onChange editor))))
                             js/undefined)
                           #js [value])

        on-key-down (react/useCallback (fn [^js event]
                                         (when allow-macros
                                           (macro/auto-insert-macro-closing-curly-bracket editor event)
                                           (macro/skip-over-macro-closing-curly-bracket editor event))
                                         (when (some? on-key-down)
                                           (on-key-down editor event)))
                                       #js [editor on-key-down])
        on-key-press (react/useCallback (fn [^js event]
                                          (when (some? on-key-press)
                                            (on-key-press editor event)))
                                        #js [editor on-key-press])

        on-change (react/useCallback (fn [^js _slate-value]
                                       (let [focused (.-current focused-ref)]
                                         (when (and focused (some? on-change))
                                           (on-change editor))))
                                     #js [editor on-change])

        render-placeholder (react/useCallback (fn [props]
                                                (let [attributes (.-attributes props)
                                                      children (.-children props)]
                                                  ($ :span (-> attributes ->clj (assoc-in [:style :top] "5") ->js)

                                                     children)))
                                              #js [])
        render-element (react/useCallback (fn [^js props]
                                            (let [element (.-element props)
                                                  element-type (.-type element)
                                                  attributes (.-attributes props)
                                                  children (.-children props)]
                                              (cond
                                                (= element-type "paragraph")
                                                ($ :p attributes
                                                   children)

                                                (= element-type common/mention-node-type)
                                                ($ mentions-node/mention {:user-id (.-id element)}
                                                   children)

                                                (= element-type common/macro-content-node-type)
                                                ($ macro/macro-content-component {:attributes attributes}
                                                   children)

                                                (= element-type common/macro-node-type)
                                                ($ macro/macro-component {:valid (.-isValid element)
                                                                          :attributes attributes}
                                                   children)

                                                (= element-type common/query-param-attribute-node-type)
                                                ($ query-param/query-param-attribute-component {:attributes attributes}
                                                   children)

                                                (= element-type common/query-param-value-node-type)
                                                ($ query-param/query-param-value-component {:attributes attributes}
                                                   children)

                                                (= element-type common/query-param-node-type)
                                                ($ query-param/query-param-component {:attributes attributes}
                                                   children))))
                                          #js [])]
    ($ slate-react/Slate {:editor       editor
                          :initialValue initial-value
                          :onChange     on-change}
       ($ styled-slate-editable {:class             class
                                 :data-testid       data-testid
                                 :error             error
                                 :spellCheck        false
                                 :placeholder       placeholder
                                 :renderElement     render-element
                                 :renderPlaceholder render-placeholder
                                 :renderLeaf        leaf/leaf-component-react
                                 :onFocus           on-focus
                                 :onBlur            on-blur
                                 :onKeyDown         on-key-down
                                 :onKeyPress        on-key-press
                                 :autoFocus         auto-focus?
                                 :sx                sx}))))
